import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faSpinner,
  faEye,
  faDownload,
  faSync,
  faBan,
  faInfoCircle,
  faUpload,
  faTimes,
  faEdit,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import axios from "axios";
import { BASE_URL } from "../../config";
import * as XLSX from "xlsx";

const TEMPLATE_URL =
  "https://public-testing.ap-south-1.linodeobjects.com/collection-template.csv";

const CollectionsHistory = () => {
  const [filters, setFilters] = useState({
    billingType: "",
    seriesNo: "",
    dateFrom: "",
    dateTo: "",
    corporate: [],
    paymentStatus: "",
    billedType: "",
  });

  const [corporateSearch, setCorporateSearch] = useState("");
  const [filteredCorporations, setFilteredCorporations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [collectionsData, setCollectionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [successfulUploads, setSuccessfulUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState([]);

  const allowedFileTypes = ["xlsx", "xls"];
  const maxFileSizeMB = 20;
  const maxUploadLimit = 5;

  const phpFormatter = new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const getStatusStyles = (status) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800";
      case "Partially Paid":
        return "bg-yellow-100 text-yellow-800";
      case "Unpaid":
        return "bg-red-100 text-red-800";
      case "Overpaid":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleRowSelect =(collection) => {
    setSelectedRows((prev) => {
      if (prev.some((row) => row.series_no_uid === collection.series_no_uid)) {
        return prev.filter(
          (row) => row.series_no_uid !== collection.series_no_uid
        );
      }
      return [...prev, collection];
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelections = collectionsData.filter(
        (collection) =>
          !selectedRows.some(
            (row) => row.series_no_uid === collection.series_no_uid
          ) && collection.payment_status !== "Paid"
      );
      setSelectedRows((prev) => [...prev, ...newSelections]);
    } else {
      const currentPageIds = collectionsData.map(
        (collection) => collection.series_no_uid
      );
      setSelectedRows((prev) =>
        prev.filter((row) => !currentPageIds.includes(row.series_no_uid))
      );
    }
  };

  const handleDownloadTemplate = () => {
    if (selectedRows.length === 0) {
      toast.error("Please select at least one collection to download.", {
        duration: 4000,
      });
      return;
    }

    const data = selectedRows.map((row) => ({
      series_no_uid: row.series_no_uid,
      series_no: row.series_no,
      billed_type: row.billed_type,
      amount_paid: "",
      cm_amount: "",
      cm_no: "",
      cwt: "",
      date_collected: "",
      or_no: "",
      reference_no: "",
      cheque_no: "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Collections");

    // Set column widths for better readability
    worksheet["!cols"] = [
      { wch: 36 }, // Series Unique ID
      { wch: 15 }, // Series No
      { wch: 15 }, // Billed Type
      { wch: 15 }, // Amount Paid
      { wch: 15 }, // CM Amount
      { wch: 15 }, // CM No
      { wch: 10 }, // CWT
      { wch: 15 }, // Date Collected
      { wch: 15 }, // OR No
      { wch: 15 }, // Reference No
      { wch: 15 }, // Cheque No
    ];

    XLSX.writeFile(
      workbook,
      `Collection_Template_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.xlsx`
    );
    toast.success("Template downloaded successfully.", { duration: 4000 });
  };

  const handleBatchUpload = () => {
    setUploadModalOpen(true);
    setFiles([]);
    setSuccessfulUploads([]);
    setIsDragging(false);
  };

  const handleFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(
            ", "
          )} files allowed.`,
          { duration: 4000 }
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(
          `File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`,
          { duration: 4000 }
        );
        return false;
      }

      return true;
    });

    if (files.length + validFiles.length > maxUploadLimit) {
      toast.error(
        `You can only upload up to ${maxUploadLimit} files at a time.`,
        { duration: 4000 }
      );
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      isPolling: false,
      uploadId: null,
      error: null,
      status: null,
      summary: null,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    newFiles.forEach((fileObj) => handleFileUpload(fileObj));
  };

  const handleFileUpload = async (fileObj) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f
      )
    );
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", fileObj.file);

      const response = await axios.post(
        `${BASE_URL}/collections/file-upload/collections-data`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? { ...f, progress: percentCompleted }
                  : f
              )
            );
          },
        }
      );

      const uploadId = response.data.upload_id;
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? {
                ...f,
                isUploading: false,
                isPolling: true,
                progress: 100,
                uploadId,
              }
            : f
        )
      );
      setSuccessfulUploads((prev) => [...prev, uploadId]);
      toast.success(`File ${fileObj.file.name} uploaded successfully!`, {
        duration: 4000,
      });

      let attempts = 0;
      const maxAttempts = 60;
      const pollInterval = 3000;

      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(
            `${BASE_URL}/collections/file-upload/collections-upload-status`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "authentication/token"
                )}`,
              },
              params: { upload_id: uploadId },
            }
          );

          const upload = statusResponse.data.data;
          if (upload.status === "completed" || upload.status === "failed") {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: upload.status,
                      summary: {
                        successCount: upload.success_update_count || 0,
                        failedCount: upload.failed_update_count || 0,
                        errorMessage: upload.error_message || null,
                      },
                    }
                  : f
              )
            );
            if (upload.status === "completed") {
              toast.success(
                `File ${fileObj.file.name} processed successfully!`,
                { duration: 4000 }
              );
              fetchCollectionsHistory();
            } else {
              toast.error(
                `File ${fileObj.file.name} processing failed: ${
                  upload.error_message || "Unknown error"
                }`,
                { duration: 4000 }
              );
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, pollInterval);
          } else {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: "timeout",
                      summary: { errorMessage: "Upload processing timed out." },
                    }
                  : f
              )
            );
            toast.error(`File ${fileObj.file.name} processing timed out.`, {
              duration: 4000,
            });
          }
        } catch (error) {
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, pollInterval);
          } else {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: "error",
                      summary: {
                        errorMessage: "Error checking upload status.",
                      },
                    }
                  : f
              )
            );
            toast.error(`Error checking status for ${fileObj.file.name}.`, {
              duration: 4000,
            });
          }
        }
      };

      setTimeout(pollStatus, pollInterval);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to upload file.";
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? {
                ...f,
                isUploading: false,
                isPolling: false,
                error: errorMessage,
              }
            : f
        )
      );
      toast.error(errorMessage, { duration: 4000 });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileChange(droppedFiles);
  };

  const handleRemoveFile = async (fileObj) => {
    const result = await Swal.fire({
      title: "Remove File?",
      text: `Are you sure you want to remove ${fileObj.file.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove it",
    });

    if (result.isConfirmed) {
      setFiles((prevFiles) => prevFiles.filter((f) => f.file !== fileObj.file));
      if (fileObj.uploadId) {
        setSuccessfulUploads((prev) =>
          prev.filter((id) => id !== fileObj.uploadId)
        );
      }
      toast.success("File removed successfully.", { duration: 3000 });
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  const fetchCorporations = async (search = "") => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/corporate/data`, {
        params: { search, billing_type: filters.billingType || undefined },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      setFilteredCorporations(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch corporations.", { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleCorporateSearch = (e) => {
    const value = e.target.value;
    setCorporateSearch(value);
    fetchCorporations(value);
  };

  const handleCorporateSelect = (corp) => {
    if (!filters.corporate.some((c) => c.id === corp.id)) {
      setFilters((prev) => ({
        ...prev,
        corporate: [...prev.corporate, corp],
      }));
      setCorporateSearch("");
      setFilteredCorporations([]);
    }
  };

  const handleRemoveCorporate = (corpId) => {
    setFilters((prev) => ({
      ...prev,
      corporate: prev.corporate.filter((c) => c.id !== corpId),
    }));
  };

  const fetchCollectionsHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/collections`, {
        params: {
          billing_type: filters.billingType || undefined,
          series_no: filters.seriesNo || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          corporate: filters.corporate.map((c) => c.id),
          payment_status: filters.paymentStatus || undefined,
          billed_type: filters.billedType || undefined,
          page: currentPage,
          per_page: itemsPerPage,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });

      setCollectionsData(response.data.data);
      setTotalEntries(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error("Failed to fetch collections history.", { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCollection = (seriesNoUid) => {
    toast.info("Update collection functionality coming soon!", {
      duration: 4000,
    });
  };

  const handleCancelCollection = async (seriesNo, seriesNoUid) => {
    const result = await Swal.fire({
      title: `Are you sure you want to cancel Series Number: ${seriesNo}?`,
      text: "Please provide a reason for cancellation.",
      icon: "warning",
      input: "textarea",
      inputPlaceholder: "Enter cancellation reason...",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
      preConfirm: (reason) => {
        if (!reason) {
          Swal.showValidationMessage("Cancellation reason is required.");
        }
        return reason;
      },
    });

    if (result.isConfirmed) {
      try {
        await axios.post(
          `${BASE_URL}/collections/series/${seriesNoUid}/cancel`,
          { reason: result.value },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "authentication/token"
              )}`,
            },
          }
        );
        toast.success("Collection cancelled successfully.", { duration: 4000 });
        fetchCollectionsHistory();
      } catch (error) {
        toast.error("Failed to cancel collection.", { duration: 4000 });
      }
    }
  };

  const handleViewCancellation = (
    seriesNo,
    cancellationReason,
    cancelledByUserName
  ) => {
    Swal.fire({
      title: `Cancellation Details for Series Number: ${seriesNo}`,
      html: `
      <div style="text-align: left;">
        <p><strong>Reason:</strong> ${
          cancellationReason || "No reason provided."
        }</p>
        <p><strong>Cancelled By:</strong> ${
          cancelledByUserName || "Unknown"
        }</p>
      </div>
    `,
      icon: "info",
      confirmButtonColor: "#3085d6",
      confirmButtonText: "OK",
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchCollectionsHistory();
  }, [filters, currentPage, itemsPerPage]);


  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          onChange={handleSelectAll}
          checked={collectionsData.every(
            (collection) =>
              selectedRows.some(
                (row) => row.series_no_uid === collection.series_no_uid
              ) || collection.payment_status === "Paid"
          )}
          disabled={collectionsData.every(
            (collection) => collection.payment_status === "Paid"
          )}
        />
      ),
      sortable: false,
    },
    { key: "billing_date", label: "Billing Date", sortable: true },
    { key: "series_no", label: "SI/SOA#", sortable: true },
    { key: "corp_name", label: "Corp", sortable: true },
    { key: "billed_amount", label: "Billed Amount", sortable: true },
    { key: "delivery_date", label: "Date Delivered", sortable: true },
    { key: "amount_paid", label: "Total Amount Paid", sortable: true },
    { key: "cm_amount", label: "CM Amt", sortable: true },
    { key: "cm_no", label: "CM#", sortable: true },
    { key: "cwt", label: "CWT", sortable: true },
    { key: "balance", label: "Balance", sortable: true },
    { key: "date_collected", label: "Date Collected", sortable: true },
    { key: "or_no", label: "OR#", sortable: true },
    { key: "aging", label: "Aging (Days)", sortable: true },
    { key: "payment_status", label: "Status", sortable: true },
    { key: "billed_type", label: "Billed Type", sortable: true },
    { key: "total_interest_fee", label: "Total Interest Fee", sortable: true },
    { key: "have_interest", label: "Has Interest", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2)
      startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i
              ? "bg-indigo-600 text-white"
              : "text-gray-700 hover:bg-indigo-100"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} collections`;
  };

  const LoadingSkeleton = ({ rows = 5, columns = 18 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="p-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  const fetchCollectionHistory = async (seriesNoUid) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/collections/series/${seriesNoUid}/history`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      setSelectedHistory(response.data.data);
      setHistoryModalOpen(true);
    } catch (error) {
      toast.error("Failed to fetch collection history.", { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  // Process Manual Payment
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedSeriesNoUid, setSelectedSeriesNoUid] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount_paid: "",
    cm_amount: "",
    cm_no: "",
    cwt: "",
    date_collected: "",
    or_no: "",
    reference_no: "",
    cheque_no: "",
  });

  const handleProcessPayment = (seriesNoUid) => {
    setSelectedSeriesNoUid(seriesNoUid);
    setPaymentData({
      amount_paid: "",
      cm_amount: "",
      cm_no: "",
      cwt: "",
      date_collected: "",
      or_no: "",
      reference_no: "",
      cheque_no: "",
    });
    setPaymentModalOpen(true);
  };

  const submitPayment = async () => {
    try {
      setLoading(true);
      await axios.post(
        `${BASE_URL}/collections/series/${selectedSeriesNoUid}/payment`,
        paymentData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      toast.success("Payment processed successfully!", { duration: 4000 });
      setPaymentModalOpen(false);
      fetchCollectionsHistory();
    } catch (error) {
      toast.error("Failed to process payment.", { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <Tooltip id="filter-tooltip" />
      <div className="max-w-1xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white rounded-2xl shadow-lg">
            <FontAwesomeIcon
              icon={faSearch}
              className="text-indigo-600 text-lg"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Collections</h1>
            <p className="text-sm text-gray-500">
              View and manage collection records
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Filters
            </h2>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="billingType"
                  className="text-sm font-medium text-gray-700"
                >
                  Billing Type
                </label>
                <select
                  id="billingType"
                  value={filters.billingType}
                  onChange={(e) =>
                    handleFilterChange("billingType", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by billing type"
                >
                  <option value="">All</option>
                  <option value="SI">Sales Invoice (SI)</option>
                  <option value="SOA">Statement of Account (SOA)</option>
                </select>
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="seriesNo"
                  className="text-sm font-medium text-gray-700"
                >
                  Series Number
                </label>
                <input
                  id="seriesNo"
                  type="number"
                  value={filters.seriesNo}
                  onChange={(e) =>
                    handleFilterChange("seriesNo", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter series number"
                  min="1"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by series number"
                />
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="dateFrom"
                  className="text-sm font-medium text-gray-700"
                >
                  Billing Date From
                </label>
                <input
                  id="dateFrom"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    handleFilterChange("dateFrom", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by start date"
                />
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="dateTo"
                  className="text-sm font-medium text-gray-700"
                >
                  Billing Date To
                </label>
                <input
                  id="dateTo"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by end date"
                />
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="paymentStatus"
                  className="text-sm font-medium text-gray-700"
                >
                  Payment Status
                </label>
                <select
                  id="paymentStatus"
                  value={filters.paymentStatus}
                  onChange={(e) =>
                    handleFilterChange("paymentStatus", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by payment status"
                >
                  <option value="">All</option>
                  <option value="Paid">Paid</option>
                  <option value="Partially Paid">Partially Paid</option>
                  <option value="Unpaid">Unpaid</option>
                  <option value="Overpaid">Overpaid</option>
                </select>
              </div>
              <div className="flex-1 min-w-[150px] flex flex-col gap-1">
                <label
                  htmlFor="billedType"
                  className="text-sm font-medium text-gray-700"
                >
                  Billed Type
                </label>
                <select
                  id="billedType"
                  value={filters.billedType}
                  onChange={(e) =>
                    handleFilterChange("billedType", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Filter by billed type"
                >
                  <option value="">All</option>
                  <option value="NAF">Network Access Fee (NAF)</option>
                  <option value="CRF">Card Replacement Fee (CRF)</option>
                  <option value="CPF">Card Printing Fee (CPF)</option>
                  <option value="AF">Admin Fee (AF)</option>
                  <option value="IF">Interest Fee (IF)</option>
                </select>
              </div>
              <div className="flex-1 min-w-[200px] flex flex-col gap-1 relative">
                <label
                  htmlFor="corporate"
                  className="text-sm font-medium text-gray-700"
                >
                  Corporate
                </label>
                <input
                  id="corporate"
                  type="text"
                  placeholder="Search corporations..."
                  value={corporateSearch}
                  onChange={handleCorporateSearch}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-tooltip-id="filter-tooltip"
                  data-tooltip-content="Search and select corporations"
                />
                {filteredCorporations.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg top-full">
                    {filteredCorporations.map((corp) => (
                      <li
                        key={corp.id}
                        onClick={() => handleCorporateSelect(corp)}
                        className="px-3 py-2 text-sm hover:bg-indigo-100 cursor-pointer"
                      >
                        {corp.name}
                      </li>
                    ))}
                  </ul>
                )}
                {filters.corporate.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {filters.corporate.map((corp) => (
                      <span
                        key={corp.id}
                        className="inline-flex items-center px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full"
                      >
                        {corp.name}
                        <button
                          onClick={() => handleRemoveCorporate(corp.id)}
                          className="ml-1 text-indigo-600 hover:text-indigo-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Collections
            </h2>
            <div className="flex flex-wrap gap-4 mb-4">
              <button
                onClick={() => fetchCollectionsHistory()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                disabled={loading}
              >
                <FontAwesomeIcon
                  icon={loading ? faSpinner : faSync}
                  spin={loading}
                />
                Refresh
              </button>
              <button
                onClick={handleDownloadTemplate}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 transition-colors"
              >
                <FontAwesomeIcon icon={faDownload} />
                Download Selected Collections
              </button>
              <button
                onClick={handleBatchUpload}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 transition-colors"
              >
                <FontAwesomeIcon icon={faUpload} />
                Batch Upload Collection Data
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <LoadingSkeleton
                      rows={itemsPerPage}
                      columns={columns.length}
                    />
                  ) : collectionsData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="p-3 text-center text-gray-500 text-sm"
                      >
                        No collections found.
                      </td>
                    </tr>
                  ) : (
                    collectionsData.map((collection) => (
                      <tr
                        key={collection.series_no_uid}
                        className={`border-b hover:bg-gray-50 ${
                          collection.is_cancelled ? "bg-gray-200" : ""
                        }`}
                      >
                        <td className="p-3 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedRows.some(
                              (row) =>
                                row.series_no_uid === collection.series_no_uid
                            )}
                            onChange={() => handleRowSelect(collection)}
                            disabled={collection.payment_status === "Paid"}
                          />
                        </td>
                        <td className="p-3 text-sm">
                          {collection.billing_date}
                        </td>

                        <td className="p-3 text-sm">
                          {filters.billingType == 'SOA' ? (
                            <>AR - </>
                          ) : null}
                          {collection.series_no}
                          {collection.is_cancelled ? (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              CANCELLED
                            </span>
                          ) : null}
                        </td>

                        {/* <td className="p-3 text-sm">
                          {collection.series_no}
                          {collection.is_cancelled && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              CANCELLED
                            </span>
                          )}
                        </td> */}

                        <td className="p-3 text-sm">{collection.corp_name}</td>
                        <td className="p-3 text-sm">
                          {phpFormatter.format(collection.billed_amount)}
                        </td>
                        <td className="p-3 text-sm">
                          {collection.delivery_date ? (
                            <span className="text-green-600">
                              {collection.delivery_date}
                            </span>
                          ) : (
                            <span className="text-red-600">
                              Not Yet Delivered
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-sm">
                          {phpFormatter.format(collection.amount_paid)}
                        </td>
                        <td className="p-3 text-sm">
                          {phpFormatter.format(collection.cm_amount)}
                        </td>
                        <td className="p-3 text-sm">
                          {collection.cm_no || "-"}
                        </td>
                        <td className="p-3 text-sm">
                          {phpFormatter.format(collection.cwt)}
                        </td>
                        <td className="p-3 text-sm">
                          {phpFormatter.format(collection.balance)}
                        </td>
                        <td className="p-3 text-sm">
                          {collection.date_collected || "-"}
                        </td>
                        <td className="p-3 text-sm">
                          {collection.or_no || "-"}
                        </td>
                        <td className="p-3 text-sm">{collection.aging || "-"}</td>
                        <td className="p-3 text-sm">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusStyles(
                              collection.payment_status
                            )}`}
                          >
                            {collection.payment_status}
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          {collection.billed_type}
                        </td>
                        <td className="p-3 text-sm">
                          {phpFormatter.format(collection.total_interest_fee || 0)}
                        </td>
                        <td className="p-3 text-sm">
                          {collection.have_interest ? "Yes" : "No"}
                        </td>
                        <td className="p-3 text-sm flex gap-2">
                          <button
                            onClick={() =>
                              fetchCollectionHistory(collection.series_no_uid)
                            }
                            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                            data-tooltip-id="filter-tooltip"
                            data-tooltip-content="View collection history"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            onClick={() =>
                              handleProcessPayment(collection.series_no_uid)
                            }
                            className="text-green-600 hover:text-green-800 flex items-center gap-1"
                            data-tooltip-id="filter-tooltip"
                            data-tooltip-content="Process payment"
                          >
                            <FontAwesomeIcon icon={faMoneyBillWave} />
                          </button>
                          {!collection.is_cancelled ? (
                            <button
                              onClick={() =>
                                handleCancelCollection(
                                  collection.series_no,
                                  collection.series_no_uid
                                )
                              }
                              className="text-red-600 hover:text-red-800 flex items-center gap-1"
                              data-tooltip-id="filter-tooltip"
                              data-tooltip-content="Cancel collection"
                            >
                              <FontAwesomeIcon icon={faBan} />
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleViewCancellation(
                                  collection.series_no,
                                  collection.cancellation_reason,
                                  collection.cancelled_by_user_name
                                )
                              }
                              className="text-gray-600 hover:text-gray-800 flex items-center gap-1"
                              data-tooltip-id="filter-tooltip"
                              data-tooltip-content="View cancellation reason"
                            >
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="p-6 border-t border-gray-100">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={handleDownloadTemplate}
                  className="px-3 py-1.5 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1 transition-colors">
                  <FontAwesomeIcon icon={faDownload} /> Download Selected
                  Collections
                </button>
                <button
                  onClick={handleBatchUpload}
                  className="px-3 py-1.5 text-xs text-white bg-gray-600 rounded-lg hover:bg-gray-700 flex items-center gap-1 transition-colors"
                >
                  <FontAwesomeIcon icon={faUpload} /> Batch Upload Collection
                  Data
                </button>
              </div>
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex items-center gap-4 mb-3 md:mb-0">
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    aria-label="Items per page"
                  >
                    {[5, 10, 20, 50, 100].map((val) => (
                      <option key={val} value={val}>
                        {val} per page
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-600">
                    {getShowingEntriesText()}
                  </span>
                  <span className="text-sm text-gray-600">
                    Selected: {selectedRows.length} collections
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || loading}
                    className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    aria-label="First page"
                  >
                    <FontAwesomeIcon icon={faAngleDoubleLeft} />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    aria-label="Previous page"
                  >
                    <FontAwesomeIcon icon={faAngleLeft} />
                  </button>
                  {renderPageNumbers()}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    aria-label="Next page"
                  >
                    <FontAwesomeIcon icon={faAngleRight} />
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || loading}
                    className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                    aria-label="Last page"
                  >
                    <FontAwesomeIcon icon={faAngleDoubleRight} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {uploadModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Upload Collection Data
                </h2>
                <button
                  onClick={() => setUploadModalOpen(false)}
                  className="text-gray-600 hover:text-gray-800 text-lg"
                >
                  ×
                </button>
              </div>
              <div className="flex flex-col gap-4">
                <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Excel File Format Instructions
                  </h3>
                  <p className="text-xs text-gray-600 mb-2">
                    Ensure your Excel file follows the template format with these
                    columns:
                  </p>
                  <ul className="list-disc list-inside text-xs text-gray-600 mb-2">
                    <li>
                      <strong>Series Unique ID</strong>: Unique identifier for
                      the series.
                    </li>
                    <li>
                      <strong>Series No</strong>: Series number.
                    </li>
                    <li>
                      <strong>Billed Type</strong>: NAF = Network Access Fee,
                      CRF = Card Replacement Fee, CPF = Card Printing Fee, AF =
                      Admin Fee, IF = Interest Fee
                    </li>
                    <li>
                      <strong>Amount Paid</strong>: Amount paid by the customer.
                    </li>
                    <li>
                      <strong>CM Amount</strong>: Credit Memo amount.
                    </li>
                    <li>
                      <strong>CM NO</strong>: Credit Memo number.
                    </li>
                    <li>
                      <strong>CWT</strong>: Creditable Withholding Tax amount.
                    </li>
                    <li>
                      <strong>Date Collected</strong>: Payment date
                      (YYYY-MM-DD).
                    </li>
                    <li>
                      <strong>OR NO</strong>: Official Receipt number.
                    </li>
                    <li>
                      <strong>Reference NO</strong>: Payment reference number.
                    </li>
                    <li>
                      <strong>Cheque NO</strong>: Cheque number.
                    </li>
                  </ul>
                  <a
                    href={TEMPLATE_URL}
                    download="collection-template.csv"
                    className="inline-flex items-center px-3 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs"
                    data-tooltip-id="filter-tooltip"
                    data-tooltip-content="Download template"
                  >
                    <FontAwesomeIcon icon={faDownload} className="mr-2" />
                    Download Template
                  </a>
                </div>
                <div
                  className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 ${
                    isDragging
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-300 bg-gray-50"
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <FontAwesomeIcon
                    icon={faUpload}
                    className="text-3xl text-indigo-500 mb-2"
                  />
                  <p className="text-sm text-gray-700">
                    Drag and drop XLSX, XLS files here or{" "}
                    <button
                      type="button"
                      onClick={handleChooseFile}
                      className="text-indigo-600 font-semibold hover:text-indigo-800"
                    >
                      choose files
                    </button>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supported format: .xlsx, .xls (Max {maxFileSizeMB}MB per
                    file, up to {maxUploadLimit} files)
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept=".xlsx,.xls"
                    onChange={(e) => handleFileChange(e.target.files)}
                    className="hidden"
                  />
                </div>
                {files.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">
                      Selected Files
                    </h3>
                    <div className="flex justify-end gap-2 mb-4">
                      <button
                        onClick={() => setUploadModalOpen(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                        disabled={isUploading}
                      >
                        Close
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {files.map((fileObj, index) => (
                        <li
                          key={index}
                          className="flex items-center justify-between bg-gray-100 p-3 rounded-lg"
                        >
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-900 truncate">
                              {fileObj.file.name}
                            </p>
                            {fileObj.isUploading && (
                              <div className="relative pt-1">
                                <div className="bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${fileObj.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 mt-1 block">
                                  {fileObj.progress}% Uploading
                                </span>
                              </div>
                            )}
                            {fileObj.isPolling && (
                              <div className="flex items-center gap-2 mt-1">
                                <FontAwesomeIcon
                                  icon={faSpinner}
                                  className="text-indigo-600 text-xs animate-spin"
                                />
                                <span className="text-xs text-gray-600">
                                  Processing...
                                </span>
                              </div>
                            )}
                            {fileObj.error && (
                              <p className="text-xs text-red-600 mt-1">
                                Error: {fileObj.error}
                              </p>
                            )}
                            {fileObj.status && !fileObj.isPolling && (
                              <div className="text-xs mt-1">
                                <p
                                  className={
                                    fileObj.status === "completed"
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }
                                >
                                  Status:{" "}
                                  {fileObj.status === "completed"
                                    ? "Completed"
                                    : fileObj.status === "failed"
                                    ? "Failed"
                                    : "Timeout"}
                                </p>
                                {fileObj.summary && (
                                  <>
                                    <p>
                                      Successful Updates:{" "}
                                      {fileObj.summary.successCount || 0}
                                    </p>
                                    <p>
                                      Failed Updates:{" "}
                                      {fileObj.summary.failedCount || 0}
                                    </p>
                                    {fileObj.summary.errorMessage && (
                                      <p className="text-red-600">
                                        Error: {fileObj.summary.errorMessage}
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveFile(fileObj)}
                            className="text-red-500 hover:text-red-700"
                            disabled={fileObj.isUploading || fileObj.isPolling}
                            data-tooltip-id="filter-tooltip"
                            data-tooltip-content="Remove file"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setUploadModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                    disabled={isUploading}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {historyModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-7xl">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Collection History
                </h2>
                <button
                  onClick={() => setHistoryModalOpen(false)}
                  className="text-gray-600 hover:text-gray-800 text-lg"
                >
                  ×
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Billing Date
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Series No
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Corp Name
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Billed Amount
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Date Delivered
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Amount Paid
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        CM Amount
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        CM No
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        CWT
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Outstanding Balance
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Date Collected
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        OR No
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Total Interest Fee
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Has Interest
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Upload File
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600">
                        Created At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedHistory.length === 0 ? (
                      <tr>
                        <td
                          colSpan={16}
                          className="p-3 text-center text-gray-500 text-sm"
                        >
                          No payment history found.
                        </td>
                      </tr>
                    ) : (
                      selectedHistory.map((payment) => (
                        <tr
                          key={payment.id}
                          className={payment.is_cancelled ? "bg-gray-200" : ""}
                        >
                          <td className="p-3 text-sm">
                            {payment.billing_date || "-"}
                          </td>
                          <td className="p-3 text-sm">
                            {payment.series_no}
                            {payment.is_cancelled && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                CANCELLED
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-sm">{payment.corp_name}</td>
                          <td className="p-3 text-sm">
                            {phpFormatter.format(payment.billed_amount)}
                          </td>
                          <td className="p-3 text-sm">
                            {payment.delivery_date ? (
                              <span className="text-green-600">
                                {payment.delivery_date}
                              </span>
                            ) : (
                              <span className="text-red-600">
                                Not Yet Delivered
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-sm">
                            {phpFormatter.format(payment.amount_paid)}
                          </td>
                          <td className="p-3 text-sm">
                            {phpFormatter.format(payment.cm_amount)}
                          </td>
                          <td className="p-3 text-sm">
                            {payment.cm_no || "-"}
                          </td>
                          <td className="p-3 text-sm">
                            {phpFormatter.format(payment.cwt)}
                          </td>
                          <td className="p-3 text-sm">
                            {phpFormatter.format(payment.outstanding_balance)}
                          </td>
                          <td className="p-3 text-sm">
                            {payment.date_collected || "-"}
                          </td>
                          <td className="p-3 text-sm">
                            {payment.or_no || "-"}
                          </td>
                          <td className="p-3 text-sm">
                            {phpFormatter.format(payment.total_interest_fee || 0)}
                          </td>
                          <td className="p-3 text-sm">
                            {payment.have_interest ? "Yes" : "No"}
                          </td>
                          <td className="p-3 text-sm">
                            {payment.upload_file_name || "-"}
                          </td>
                          <td className="p-3 text-sm">{payment.created_at}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setHistoryModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {paymentModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Process Payment
                </h2>
                <button
                  onClick={() => setPaymentModalOpen(false)}
                  className="text-gray-600 hover:text-gray-800 text-lg"
                >
                  ×
                </button>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="amount_paid"
                    className="text-sm font-medium text-gray-700"
                  >
                    Amount Paid
                  </label>
                  <input
                    id="amount_paid"
                    type="number"
                    value={paymentData.amount_paid}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        amount_paid: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter amount paid"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="cm_amount"
                    className="text-sm font-medium text-gray-700"
                  >
                    CM Amount
                  </label>
                  <input
                    id="cm_amount"
                    type="number"
                    value={paymentData.cm_amount}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        cm_amount: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter CM amount"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="cm_no"
                    className="text-sm font-medium text-gray-700"
                  >
                    CM No
                  </label>
                  <input
                    id="cm_no"
                    type="text"
                    value={paymentData.cm_no}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, cm_no: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter CM number"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="cwt"
                    className="text-sm font-medium text-gray-700"
                  >
                    CWT
                  </label>
                  <input
                    id="cwt"
                    type="number"
                    value={paymentData.cwt}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, cwt: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter CWT amount"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="date_collected"
                    className="text-sm font-medium text-gray-700"
                  >
                    Date Collected
                  </label>
                  <input
                    id="date_collected"
                    type="date"
                    value={paymentData.date_collected}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        date_collected: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="or_no"
                    className="text-sm font-medium text-gray-700"
                  >
                    OR No
                  </label>
                  <input
                    id="or_no"
                    type="text"
                    value={paymentData.or_no}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, or_no: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter OR number"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="reference_no"
                    className="text-sm font-medium text-gray-700"
                  >
                    Reference No
                  </label>
                  <input
                    id="reference_no"
                    type="text"
                    value={paymentData.reference_no}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        reference_no: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter reference number"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="cheque_no"
                    className="text-sm font-medium text-gray-700"
                  >
                    Cheque No
                  </label>
                  <input
                    id="cheque_no"
                    type="text"
                    value={paymentData.cheque_no}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        cheque_no: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter cheque number"
                  />
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setPaymentModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitPayment}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    disabled={loading}
                  >
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Submit Payment"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionsHistory;